import * as React from 'react'
import { components, type GroupBase, type MenuListProps } from 'react-select'
import AsyncReactSelect from 'react-select/async'

import { useTranslatable } from 'src/hooks/locale/utils'
import { useTheme } from 'src/state/providers/Theme'

function ScrollSelectedMenuList<Opt, IsMulti extends boolean, Group extends GroupBase<Opt> = GroupBase<Opt>>(
  props: MenuListProps<Opt, IsMulti, Group> & {
    readonly MenuListComponent: React.ComponentType<MenuListProps<Opt, IsMulti, Group>>
  }
): React.ReactElement {
  const { MenuListComponent, innerRef, ...menuListProps } = props
  const listNode = React.useRef<HTMLDivElement | null>(null)

  const setRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      listNode.current = el
      if (typeof innerRef === 'function') {
        innerRef(el)
      } else if (innerRef != null) {
        ;(innerRef as React.MutableRefObject<HTMLDivElement | null>).current = el
      }
    },
    [innerRef]
  )

  React.useLayoutEffect(() => {
    const root = listNode.current
    if (root == null) {
      return
    }
    const selectedEl = root.querySelector<HTMLElement>('[aria-selected="true"]')
    selectedEl?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [])

  return <MenuListComponent {...menuListProps} innerRef={setRef} />
}

interface StyleProps {
  readonly hasError?: boolean
  readonly controlStyles?: { [key: string]: string | number }
  readonly menuPortalStyles?: { [key: string]: string | number }
}

type AsyncSelectProps<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = StyleProps & React.ComponentProps<typeof AsyncReactSelect<Option, IsMulti, Group>>

export default function AsyncSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: AsyncSelectProps<Option, IsMulti, Group>): React.ReactElement {
  const t = useTranslatable()
  const theme = useTheme()

  const mergedComponents = React.useMemo(() => {
    const UserMenuList = props.components?.MenuList
    const MenuListComponent = (UserMenuList ?? components.MenuList) as React.ComponentType<
      MenuListProps<Option, IsMulti, Group>
    >
    return {
      ...props.components,
      MenuList: (menuListProps: MenuListProps<Option, IsMulti, Group>) => (
        <ScrollSelectedMenuList {...menuListProps} MenuListComponent={MenuListComponent} />
      ),
    }
  }, [props.components])

  return (
    <AsyncReactSelect
      {...props}
      components={mergedComponents}
      noOptionsMessage={() => t('common:no_options')}
      placeholder={
        props.placeholder === undefined
          ? t('common:select')
          : typeof props.placeholder === 'string'
            ? props.placeholder
            : null
      }
      styles={{
        control: (base) =>
          ({
            ...base,
            borderColor: props.hasError === true ? '#e13834' : theme === 'dark' ? '#525252' : '#ced4da',
            background: theme === 'dark' ? '#323232' : '#ffffff',
            color: theme === 'dark' ? '#ffffff' : '#000000',
            height: props.isMulti ? 'auto' : 48,
            minHeight: props.isMulti ? 48 : 'unset',
            ...props.controlStyles,
          }) as typeof base,
        menu: (base) =>
          ({
            ...base,
            zIndex: 9999,
          }) as typeof base,
        option: (base, { isFocused, isSelected }) =>
          ({
            ...base,
            color: isSelected
              ? '#ffffff'
              : isFocused
                ? theme === 'dark'
                  ? '#ffffff'
                  : '#212529'
                : theme === 'dark'
                  ? '#ffffff'
                  : '#212529',
            backgroundColor: isSelected
              ? '#2684FF'
              : isFocused
                ? theme === 'dark'
                  ? '#222222'
                  : '#e0e0e0'
                : theme === 'dark'
                  ? '#323232'
                  : '#ffffff',
          }) as typeof base,
        menuPortal: (base) =>
          ({
            ...base,
            zIndex: 2000,
            ...props.menuPortalStyles,
          }) as typeof base,
        menuList: (base) =>
          ({
            ...base,
            background: theme === 'dark' ? '#323232' : '#ffffff',
          }) as typeof base,
        singleValue: (base) =>
          ({
            ...base,
            color: theme === 'dark' ? '#ffffff' : '#212529',
            zIndex: 2,
          }) as typeof base,
        valueContainer: (base) =>
          ({
            ...base,
            color: 'green',
          }) as typeof base,
        input: (base) =>
          ({
            ...base,
            color: theme === 'dark' ? '#ffffff' : '#212529',
          }) as typeof base,
      }}
    />
  )
}
